/**
 * Composable for Applying Referral Token
 * Универсальная функция для применения реферального токена после авторизации/регистрации
 * 
 * Follows DRY, SOLID principles and Vue.js Style Guide
 */

import { consola } from 'consola';
import { useReferralTokenHandler } from './useReferralTokenHandler';
import { useReferral } from './useReferral';

const logger = consola.withTag('apply-referral-token');

/**
 * Применяет реферальный токен после успешной авторизации/регистрации
 * 
 * ✅ Работает для всех типов авторизации:
 * - Регистрация через пароль
 * - Авторизация через Яндекс ID
 * - Авторизация через Telegram
 * 
 * @returns Promise<boolean> - true если токен был применен успешно, false если не было токена или ошибка
 */
export const useApplyReferralToken = async (): Promise<boolean> => {
  if (!process.client) return false;

  try {
    const tokenHandler = useReferralTokenHandler();
    const referral = useReferral();
    
    const pendingToken = tokenHandler.getPendingToken();
    
    if (!pendingToken) {
      if (process.dev) {
        logger.debug('Реферальный токен не найден, пропускаем применение');
      }
      return false;
    }

    logger.info('🎁 Применение реферального токена после авторизации:', pendingToken);
    
    // Собираем дополнительную информацию для безопасности
    const ipAddress = undefined; // Можно получить через API если нужно
    const deviceId = undefined; // Можно получить из localStorage если есть
    const userAgent = navigator.userAgent;
    
    const applyResult = await referral.applyReferralCode({
      referralToken: pendingToken,
      ipAddress,
      deviceId,
      userAgent,
    });
    
    if (applyResult) {
      logger.success('✅ Реферальный код успешно применен');
      // Очищаем токен после успешного применения
      tokenHandler.clearToken();
      return true;
    } else {
      logger.warn('⚠️ Не удалось применить реферальный код');
      // Не очищаем токен при ошибке - возможно нужно повторить
      return false;
    }
  } catch (error: any) {
    // Не блокируем авторизацию при ошибке применения реферального кода
    logger.warn('Ошибка применения реферального кода (не критично):', error);
    return false;
  }
};

